'use client';
import { Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Icon } from './Icon';
import { useLayerLock } from '../lib/useLayerLock';
import { useSite, useT } from '../lib/i18n';
import { EVICTED_PARAM, needsLogin } from '../lib/site-shared';

/**
 * 알림창 — **디자이너 시안(alertguide.html)의 `.modal-alert` 에서 제목만 뺀 것**.
 * /ux-lab §15 에서 고르던 값이 확정되어(2026-08-27 사용자) 실물로 승격했다:
 * **세로 가운데 · 버튼 꽉 채움 · 폭 400px**. 조절판 없이 이 한 모양만 쓴다.
 * (폭은 `web.css` 의 `.modal-alert` 에 박아 뒀다 — 가이드 재복사에서 지켜야 하는 값이다.)
 *
 * **토스트와 쓰는 자리가 다르다** — 담기·찜처럼 알리기만 하는 것은 토스트(`Toast.tsx`),
 * 여기는 **사용자가 읽고 넘어가야 하거나 결정해야 할 때**만 쓴다.
 *
 * 쓰기 — 어디서든 `showAlert({ message })`. 층간 전달(props) 없이 이벤트로 받는다.
 * `<AlertHost />` 는 레이아웃에 한 번만 둔다.
 *
 * **닫기는 버튼으로만** 한다(프로젝트 규칙) — 딤을 눌러도, Esc 를 눌러도 닫히지 않는다.
 * 읽히려고 띄운 것이 스쳐 지나가면 안 되기 때문이다.
 */

export type AlertKind = 'info' | 'success' | 'warning' | 'danger';

export interface AlertReq {
  message: string;
  kind?: AlertKind;
  /** 확인 버튼 글자 — 없으면 '확인' */
  okLabel?: string;
  /** 확인을 누르면 갈 곳 — 주면 그 주소로 옮긴다(세션이 끊겼을 때의 로그인 화면 등) */
  href?: string;
  /**
   * 확인을 누르면 **창을 닫는다** — 플레이어처럼 팝업으로 뜬 화면의 차단 알림(2026-09-16 사용자 지시). 닫힌 뒤엔 그 창을 연
   * 원래 화면이 그대로 보인다. 스크립트가 연 창이 아니면(탭에 직접 들어온 경우) 브라우저가 닫기를 거부하므로 `href` 로 물러난다.
   */
  closeWindow?: boolean;
  /**
   * 옮겨 가기 전에 로그인 쿠키를 지운다 — 세션이 이미 죽은 알림에 쓴다.
   * 셸이 서버에서 알아챈 경우(`evicted`)에 필요하다: 서버 컴포넌트는 쿠키를 쓸 수 없어
   * 쿠키가 남고, 그대로 두면 이미 죽은 토큰으로 매 페이지가 `/auth/me` 를 부른다.
   */
  signOut?: boolean;
  /**
   * 되묻는 자리 — 취소 버튼이 붙고, 무엇을 눌렀는지 부르는 쪽이 받는다.
   * 이 값이 있으면 `confirmLayer()` 가 만든 확인창이다(§15 — `window.confirm()` 대체).
   */
  resolve?: (ok: boolean) => void;
  /** 취소 버튼 글자 — 없으면 '취소' */
  cancelLabel?: string;
}

const EVENT = 'app:alert';

export function showAlert(req: AlertReq) {
  window.dispatchEvent(new CustomEvent<AlertReq>(EVENT, { detail: req }));
}

/**
 * 확인창 — **`window.confirm()` 대체**(§15, 2026-08-28 사용자 지시로 실물 반영).
 *
 * 브라우저 것은 모양을 손댈 수 없고 사이트와 따로 논다. 쓰는 법은 그대로다 —
 * `if (!(await confirmLayer(t('cart.removeConfirm')))) return;`
 *
 * 되돌릴 수 없는 일(삭제·탈퇴)에는 `kind: 'danger'` 를 준다.
 */
export function confirmLayer(message: string, opts?: {
  kind?: AlertKind; okLabel?: string; cancelLabel?: string;
}): Promise<boolean> {
  return new Promise((resolve) => {
    showAlert({ message, kind: opts?.kind ?? 'warning', okLabel: opts?.okLabel, cancelLabel: opts?.cancelLabel, resolve });
  });
}

/** 시안 ALERT_ICONS 와 같은 짝 — 아이콘 이름은 태그 밖 상수로 둔다(check-icons 가 훑는다) */
const ICON_INFO = 'info';
const ICON_SUCCESS = 'check';
const ICON_WARNING = 'triangle-alert';
const ICON_DANGER = 'circle-alert';
const ICON: Record<AlertKind, string> = {
  info: ICON_INFO, success: ICON_SUCCESS, warning: ICON_WARNING, danger: ICON_DANGER,
};

/**
 * `evicted` — 셸이 서버에서 알아챈 동시접속 축출(`getSession().evicted`).
 * 문구는 여기서 만든다 — 서버가 번역까지 해서 넘길 이유가 없다.
 *
 * `useSearchParams` 를 쓰므로 Suspense 로 감싼다(Next 규약).
 */
export function AlertHost(props: { evicted?: boolean }) {
  return <Suspense fallback={null}><AlertHostInner {...props} /></Suspense>;
}

/** 우리 BFF 를 부른 것인가 — 남의 주소로 나간 요청까지 세션 신호로 읽으면 안 된다 */
function isOurApi(input: RequestInfo | URL): boolean {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  if (url.startsWith('/api/')) return true;
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin && u.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

function AlertHostInner({ evicted = false }: { evicted?: boolean }) {
  const t = useT();
  const pathname = usePathname();
  const router = useRouter();
  const { loggedIn } = useSite();
  /** 셸을 다시 그린 적 있나 — 서버가 여전히 로그인으로 보면 무한히 돌 수 있어 한 번만 한다 */
  const refreshed = useRef(false);
  /**
   * 축출 알림 — `goLogin` 은 확인을 누른 뒤 로그인 화면으로 옮길지다.
   * **이미 로그인 화면에 있는 경우**(관문이 보낸 `?evicted=1`)는 옮기지 않고 닫기만 한다.
   * 어느 쪽이든 죽은 쿠키는 지운다 — 남겨 두면 매 페이지가 죽은 토큰으로 서버를 부른다.
   *
   * **취소도 둔다**(2026-08-28 사용자 지시) — 지금 당장 다시 로그인할 생각이 없을 수도 있다.
   * 취소하면 보던 화면에 따라 갈린다: **로그인해야 볼 수 있는 화면이면 홈으로**(그 자리에
   * 남겨 둘 수 없다), 아니면 **창만 닫는다**. 어느 쪽이든 죽은 쿠키는 지운다 — 남기면
   * 다음 화면에서 같은 알림이 또 뜬다.
   */
  const evictedAlert = (goLogin: boolean): AlertReq => ({
    message: t('acc.evicted'), kind: 'warning',
    okLabel: t('acc.evicted.ok'), cancelLabel: t('action.cancel'),
    signOut: true, ...(goLogin ? { href: '/login' } : {}),
    resolve: (ok) => {
      if (ok) return; // 확인은 아래 close() 가 쿠키 정리 + 이동까지 맡는다
      const home = needsLogin(window.location.pathname) ? '/' : null;
      void fetch('/api/auth/logout', { method: 'POST' })
        .catch(() => undefined)
        .finally(() => { if (home) window.location.href = home; });
    },
  });
  /*
   * 처음에는 늘 비어 있다 — 포털은 `document.body` 에 그리므로 브라우저에 올라온 뒤에만
   * 그릴 수 있고, 서버 렌더에서 내용이 차 있으면 `createPortal` 이 터진다.
   * 축출 알림도 아래 효과에서 붙는다(붙은 다음 프레임 — 눈에 띄는 차이는 없다).
   */
  const [cur, setCur] = useState<AlertReq | null>(null);

  useEffect(() => {
    const on = (e: Event) => setCur((prev) => prev ?? (e as CustomEvent<AlertReq>).detail);
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);

  /*
   * 동시접속 축출을 브라우저에서 알아채는 지점(2026-08-27) — **여기 한 곳뿐이다**.
   *
   * 화면에서 API 를 부르는 자리가 쉰 곳이 넘는데, 그 전부가 BFF(`/api/…`)를 지난다.
   * BFF 가 축출 401 에 `x-session-evicted` 를 붙여 주므로 `fetch` 를 한 겹 감싸 그것만
   * 본다 — 호출부를 한 곳도 고치지 않고 전 화면이 같은 알림을 받는다.
   * 본문은 건드리지 않는다(헤더만 읽으므로 스트림을 소비하지 않는다).
   */
  useEffect(() => {
    const orig = window.fetch;
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const res = await orig(...args);
      if (res.status === 401 && res.headers.get('x-session-evicted')) showAlert(evictedAlert(true));
      /*
       * 셸이 **로그인 상태로 굳은** 경우(2026-09-04 사용자 제보) — 비로그인인데 모바일 메뉴가
       * "최근 수강내역 불러오는 중" 을 그리고 있었다.
       *
       * 로그인 여부는 서버 렌더가 `/auth/me` 로 판단해 셸에 심는다. 그런데 **셸(레이아웃)은
       * 클라이언트 이동에서 다시 그려지지 않는다** — 그 사이 세션이 끝나면(만료·다른 곳에서
       * 로그아웃) 화면은 계속 로그인 상태로 남고, 그 상태에서 API 를 부르면 401 이 온다.
       * 401 은 **서버가 아니라고 말한 것**이므로 그때 셸을 다시 받는다 — 헤더도 메뉴도 함께
       * 비로그인으로 돌아온다.
       */
      if (res.status === 401 && loggedIn && !refreshed.current && isOurApi(args[0])) {
        refreshed.current = true;
        router.refresh();
      }
      return res;
    };
    return () => { window.fetch = orig; };
    // t 는 문맥이 그대로면 같은 함수다(useT) — 언어가 바뀔 때만 다시 감싼다
  }, [t, loggedIn, router]);

  /*
   * 축출을 알아채는 나머지 두 갈래 — **셸이 서버에서 본 것**(`evicted`)과
   * **관문이 주소에 실어 보낸 것**(`?evicted=1`).
   *
   * 주소 갈래는 사용자 제보로 생겼다(2026-08-27) — 장바구니·내 강의실처럼 로그인이
   * 필요한 자리로 옮기면 관문(`requireMe`)이 그 자리에서 로그인 화면으로 보내 버려,
   * 셸이 알림을 그릴 새가 없었다. 아무 말 없이 로그아웃된 것처럼 보였다.
   *
   * 이미 로그인 화면에 있으면 **옮기지 않고 닫기만** 한다 — 갈 곳에 이미 와 있다.
   * 주소의 표시는 **띄우자마자 지운다** — 남겨 두면 새로고침할 때마다 다시 뜬다.
   */
  const evictedInUrl = useSearchParams().get(EVICTED_PARAM) === '1';
  /*
   * **한 번 닫았으면 다시 띄우지 않는다**(2026-08-28 사용자 제보로 고침).
   *
   * 셸(layout)은 클라이언트 라우팅에서 **다시 그려지지 않는다** — 서버가 준 `evicted` 가
   * true 인 채로 계속 남아 있고, 페이지를 옮길 때마다 `pathname` 이 바뀌어 아래 효과가
   * 다시 돈다. 그래서 취소를 눌러도 **옮길 때마다 같은 알림이 다시 떴다**.
   * 전체 새로고침이면 이 표시가 초기화되지만, 그때는 쿠키가 이미 지워져 `evicted` 가 false 다.
   */
  const dismissed = useRef(false);
  useEffect(() => {
    if (dismissed.current) return;
    if (!evicted && !evictedInUrl) return;
    setCur((prev) => prev ?? evictedAlert(pathname !== '/login'));
    if (!evictedInUrl) return;
    const url = new URL(window.location.href);
    url.searchParams.delete(EVICTED_PARAM);
    window.history.replaceState(null, '', url.toString());
    // t 가 바뀌면(언어 전환) 문구도 그 언어로 만들어진다
  }, [evicted, evictedInUrl, pathname, t]);

  useLayerLock(cur != null);

  if (!cur) return null;
  const kind = cur.kind ?? 'info';
  /** 되묻는 자리인가 — 취소가 붙고, 무엇을 눌렀는지 부르는 쪽이 받는다 */
  const asking = !!cur.resolve;

  const close = (ok: boolean) => {
    /*
     * **축출 알림을 닫았을 때만** 표시를 남긴다(`signOut` 이 그 표식이다).
     * 모든 알림에 세우면, 장바구니 삭제 확인창 하나를 닫은 뒤로는 진짜 축출이 일어나도
     * 알림이 안 뜬다 — 끄는 범위를 이 알림 하나로 좁힌다.
     */
    if (cur.signOut) dismissed.current = true;
    cur.resolve?.(ok);
    // 옮겨 갈 곳이 있으면 닫지 않고 그대로 이동한다. 없으면 그냥 닫는다.
    const done = () => {
      if (ok && cur.closeWindow) { window.close(); if (window.closed) return; }
      if (ok && cur.href) window.location.href = cur.href; else setCur(null);
    };
    // 죽은 쿠키를 지워야 하는 알림이면 먼저 끝낸다 — 남기면 매 페이지가 죽은 토큰을 보낸다
    if (ok && cur.signOut) void fetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined).finally(done);
    else done();
  };

  return createPortal(
    <>
      <div className="modal-backdrop open" />
      <div className="modal modal-alert open" role="alertdialog" aria-modal="true">
        <div className="modal-body">
          <div className={`modal-alert-icon ${kind}`}><Icon name={ICON[kind]} /></div>
          {/* 제목(.modal-alert-title)은 두지 않는다 — 사용자 확정(2026-08-26) */}
          <p className="modal-alert-msg">{cur.message}</p>
          {/* 버튼은 꽉 채움(§15 확정) — 취소는 되묻는 자리에만 붙는다 */}
          <div className="modal-alert-actions">
            {asking && (
              <button className="action-btn action-cancel" type="button" onClick={() => close(false)}>
                {cur.cancelLabel ?? t('action.cancel')}
              </button>
            )}
            <button className="action-btn action-submit" type="button" onClick={() => close(true)}>
              {cur.okLabel ?? t('action.confirm')}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
